import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Radio,
  RadioGroup,
  Stack,
  Text,
  Textarea,
  useToast,
  VStack,
  Badge,
  Card,
  CardHeader,
  CardBody,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Checkbox,
} from '@chakra-ui/react';
import { 
  FiArrowLeft, 
  FiSave, 
  FiSend, 
  FiCheck, 
  FiInfo
} from 'react-icons/fi';
import { useForm, Controller } from 'react-hook-form';
// API config is imported through the service modules
import { useAuth } from '../../context/AuthContext';
import unifiedService from '../../services/api/unifiedService';
import { EVALUATION_STATUS } from '../../utils/evaluationStatusUtils';

/**
 * EvaluationForm component
 * Committee page for evaluating candidates
 */
const EvaluationForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  const { isOpen, onOpen, onClose } = useDisclosure();
  
  // State
  const [candidate, setCandidate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [existingEvaluation, setExistingEvaluation] = useState(null);
  const [totalScore, setTotalScore] = useState(0);
  const [checklistPassed, setChecklistPassed] = useState(true);
  
  // Form state
  const {
    handleSubmit,
    control,
    register,
    setValue,
    watch,
    getValues,
    reset,
  } = useForm();
  
  // Watch all criteria scores to calculate total
  const watchAllFields = watch();
  
  // Checklist verification items
  const checklistItems = useMemo(() => [
    { id: 'residence_verification', name: 'أن يكون المرشح من سكان الحي كما يوضحه العنوان الوطني' },
    { id: 'minimum_subscription_paid', name: 'أن يكون سدد الحد الأدنى للاشتراك السنوي وفق ما تقرره لوائح الجمعية' },
    { id: 'membership_fee_2025_paid', name: 'هل تم سداد رسوم اشتراك العضوية لعام 2025' },
    { id: 'security_clearance_verified', name: 'التحقق من صحة استمارة المسح الأمني' },
    { id: 'cv_verified', name: 'التحقق من صحة السيرة الذاتية' },
    { id: 'evaluation_criteria_available', name: 'توفر معايير تقييم المرشحين' },
  ], []);
  
  // Written questions evaluation criteria (1-3 scale)
  const writtenQuestionsCriteria = useMemo(() => [
    { id: 'society_knowledge_score', name: 'معلومات المرشح عن جمعية مراكز الأحياء', weight: 25 },
    { id: 'social_responsibility_score', name: 'مدى المشاركة في المسؤولية الاجتماعية', weight: 25 },
    { id: 'vision_2030_score', name: 'تقييم المقترحات والأفكار التي تساهم بتحقيق أهداف وبرامج ومبادرات الجمعية بما يتوافق مع رؤية المملكة 2030', weight: 25 },
    { id: 'center_plan_score', name: 'تقييم الخطة المقدمة لتفعيل مجلس الحي وتنمية الموارد', weight: 25 },
  ], []);
  
  // Fetch candidate data and evaluation settings
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch candidate data using unifiedService
        const candidateResponse = await unifiedService.getCandidateById(id);
        
        // Make sure the candidate has the expected structure
        if (!candidateResponse) {
          throw new Error('No candidate data returned');
        }
        
        // Ensure nomination property exists for backward compatibility
        if (!candidateResponse.nomination) {
          // If the API returns the nomination directly (not nested)
          // Create a nomination property with the id for compatibility with the form
          candidateResponse.nomination = {
            id: candidateResponse.id
          };
        }
        
        setCandidate(candidateResponse);
        
        // Determine nomination id and load evaluations
        const nominationIdForEval = candidateResponse.nomination?.id || candidateResponse.id || id;
        const evaluations = await unifiedService.getNominationEvaluations(nominationIdForEval);

        // unifiedService returns response.data directly. It may be an array or an object
        const raw = Array.isArray(evaluations) ? evaluations : (evaluations ? [evaluations] : []);
        const selected = raw.find(e => e.status === 'draft') || raw[0] || null;

        if (selected) {
          setExistingEvaluation(selected);

          // Checklist values
          checklistItems.forEach(item => {
            if (selected[item.id] !== undefined) {
              setValue(item.id, selected[item.id]);
            }
          });

          // Written questions values
          writtenQuestionsCriteria.forEach(criteria => {
            if (selected[criteria.id] !== undefined && selected[criteria.id] !== null) {
              setValue(criteria.id, selected[criteria.id]);
            }
          });

          // Legacy criteria, if any
          if (selected.criteriaScores) {
            selected.criteriaScores.forEach((criteriaScore) => {
              setValue(`criteria.${criteriaScore.criteriaId}`, criteriaScore.score);
            });
          }

          // Recommendation and comments (backend uses 'comments')
          setValue('recommendation', selected.recommendation ?? '');
          setValue('comments', selected.comments ?? '');
        }
      } catch (error) {
        toast({
          title: 'خطأ في جلب البيانات',
          description: 'حدث خطأ أثناء جلب بيانات المرشح',
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top',
        });
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, toast, reset, checklistItems, writtenQuestionsCriteria, user]);

  // Check if all checklist items are verified
  useEffect(() => {
    const checklistFields = checklistItems.map(item => item.id);
    const allChecked = checklistFields.every(field => watchAllFields[field] === true);
    setChecklistPassed(allChecked);
  }, [watchAllFields, checklistItems]);

  // Calculate total score whenever criteria scores change
  useEffect(() => {
    // First check if we're using the new scoring system (written questions)
    const hasWrittenScores = writtenQuestionsCriteria.some(criteria => 
      watchAllFields[criteria.id] !== undefined && watchAllFields[criteria.id] !== null
    );
    
    if (hasWrittenScores) {
      let total = 0;
      let totalWeight = 0;
      
      writtenQuestionsCriteria.forEach((criteria) => {
        const score = watchAllFields[criteria.id];
        if (score) {
          // Convert 1-3 scale to a 0-10 scale (1≈3.33, 2≈6.67, 3=10)
          const normalizedScore = (score / 3) * 10;
          total += (normalizedScore * criteria.weight);
          totalWeight += criteria.weight;
        }
      });
      
      // Calculate weighted average on a 0-10 scale
      const weightedAverage = totalWeight > 0 ? Math.round((total / totalWeight) * 10) / 10 : 0;
      
      // If checklist is not passed, set score to 0
      setTotalScore(checklistPassed ? weightedAverage : 0);
    }
  }, [watchAllFields, writtenQuestionsCriteria, checklistPassed, checklistItems]);

  // Handle saving draft evaluation
  const handleSaveDraft = async (data) => {
    try {
      setSaving(true);
      
      // Check if candidate exists
      if (!candidate) {
        throw new Error('Candidate data is missing');
      }
      
      // Get the nomination ID - either from the nomination object or directly from the candidate
      // This handles both data structures: {id, nomination: {id}} and {id} (where id is the nomination id)
      const nominationId = candidate.nomination?.id || candidate.id;
      
      if (!nominationId) {
        throw new Error('Nomination ID is missing');
      }
      
      // Get evaluator ID from user context or localStorage as fallback
      let evaluatorId;
      if (user && user.id) {
        evaluatorId = user.id;
      } else {
        // Try to get user from localStorage - check the correct key 'userData'
        try {
          // First try 'userData' which is used by AuthContext
          const userDataStr = localStorage.getItem('userData');
          if (userDataStr) {
            const storedUser = JSON.parse(userDataStr);
            if (storedUser && storedUser.id) {
              evaluatorId = storedUser.id;
            }
          }
          
          // If not found, try 'user' key as fallback
          else {
            const userStr = localStorage.getItem('user');
            if (userStr) {
              const storedUser = JSON.parse(userStr);
              if (storedUser && storedUser.id) {
                evaluatorId = storedUser.id;
              }
            }
          }
          
          // If we still don't have a valid user, use a hardcoded committee member ID
          if (!evaluatorId) {
            // This is a temporary workaround based on the branch-center relationship issue
            console.warn('Using fallback committee member ID due to missing user data');
            evaluatorId = 1; // Default committee member ID
          }
        } catch (e) {
          console.error('Error parsing user data from localStorage:', e);
          // Use fallback ID as last resort
          evaluatorId = 1; // Default committee member ID
        }
      }
      
      // Prepare evaluation data - using snake_case keys as expected by the backend API
      const evaluationData = {
        nomination_id: nominationId,
        evaluator_id: evaluatorId,
        status: EVALUATION_STATUS.DRAFT,
        recommendation: data.recommendation || null,
        comments: data.comments || '', // Changed from notes to comments to match backend schema
        total_score: totalScore,
      };
      
      // Add checklist items
      checklistItems.forEach(item => {
        evaluationData[item.id] = data[item.id] || false;
      });
      
      // Add written questions scores
      writtenQuestionsCriteria.forEach(criteria => {
        evaluationData[criteria.id] = data[criteria.id] || null;
      });
      
      // Add legacy criteria scores for backward compatibility
      if (data.criteria) {
        evaluationData.criteriaScores = Object.keys(data.criteria).map(criteriaId => ({
          criteriaId: parseInt(criteriaId),
          score: data.criteria[criteriaId],
        }));
      }
      
      // Send to API using unifiedService
      let response;
      if (existingEvaluation) {
        response = await unifiedService.updateEvaluation(existingEvaluation.id, evaluationData);
      } else {
        response = await unifiedService.submitEvaluation(evaluationData);
      }
      
      setExistingEvaluation(response.data);
      
      toast({
        title: 'تم الحفظ',
        description: 'تم حفظ التقييم كمسودة',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
    } catch (error) {
      toast({
        title: 'خطأ في الحفظ',
        description: error.message === 'Candidate or nomination data is missing' ?
          'بيانات المرشح أو الترشيح غير متوفرة' : 'حدث خطأ أثناء حفظ التقييم',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
      console.error('Error saving evaluation:', error);
    } finally {
      setSaving(false);
    }
  };
  
  // Handle submitting final evaluation
  const handleSubmitEvaluation = async () => {
    try {
      setSubmitting(true);
      
      // Get form data
      const data = getValues();
      
      // Minimal validation: require all checklist items and all written scores before submit
      // 1) Checklist must all be true
      const checklistMissing = checklistItems.filter(item => data[item.id] !== true);
      if (checklistMissing.length > 0) {
        toast({
          title: 'نقص في قائمة التحقق',
          description: 'يجب التحقق من جميع عناصر قائمة التحقق قبل تقديم التقييم.',
          status: 'warning',
          duration: 4000,
          isClosable: true,
          position: 'top',
        });
        setSubmitting(false);
        return;
      }

      // 2) All written questions must have a score (1-3)
      const scoresMissing = writtenQuestionsCriteria.filter(c => data[c.id] === undefined || data[c.id] === null);
      if (scoresMissing.length > 0) {
        toast({
          title: 'اكتمل التقييم أولاً',
          description: 'الرجاء تقييم جميع الأسئلة المكتوبة (4 أسئلة) قبل التقديم.',
          status: 'warning',
          duration: 4000,
          isClosable: true,
          position: 'top',
        });
        setSubmitting(false);
        return;
      }
       
      // Check if candidate exists
      if (!candidate) {
        throw new Error('Candidate data is missing');
      }
      
      // Get the nomination ID - either from the nomination object or directly from the candidate
      // This handles both data structures: {id, nomination: {id}} and {id} (where id is the nomination id)
      const nominationId = candidate.nomination?.id || candidate.id;
      
      if (!nominationId) {
        throw new Error('Nomination ID is missing');
      }
      
      // Get evaluator ID from user context or localStorage as fallback
      let evaluatorId;
      if (user && user.id) {
        evaluatorId = user.id;
      } else {
        // Try to get user from localStorage
// Try to get user from localStorage - check the correct key 'userData'
try {
  // First try 'userData' which is used by AuthContext
  const userDataStr = localStorage.getItem('userData');
  if (userDataStr) {
    const storedUser = JSON.parse(userDataStr);
    if (storedUser && storedUser.id) {
      evaluatorId = storedUser.id;
    }
  }
  
  // If not found, try 'user' key as fallback
  else {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      const storedUser = JSON.parse(userStr);
      if (storedUser && storedUser.id) {
        evaluatorId = storedUser.id;
      }
    }
  }
  
  // If we still don't have a valid user, use a hardcoded committee member ID
  if (!evaluatorId) {
    // This is a temporary workaround based on the branch-center relationship issue
    console.warn('Using fallback committee member ID due to missing user data');
    evaluatorId = 1; // Default committee member ID
  }
} catch (e) {
  console.error('Error parsing user data from localStorage:', e);
  // Use fallback ID as last resort
  evaluatorId = 1; // Default committee member ID
}
      }
      
      // Prepare evaluation data - using snake_case keys as expected by the backend API
      const evaluationData = {
        nomination_id: nominationId,
        evaluator_id: evaluatorId,
        status: EVALUATION_STATUS.SUBMITTED,
        recommendation: data.recommendation || null,
        comments: data.comments || '', // Changed from notes to comments to match backend schema
        total_score: totalScore,
      };
      
      // Add checklist items
      checklistItems.forEach(item => {
        evaluationData[item.id] = data[item.id] || false;
      });
      
      // Add written questions scores
      writtenQuestionsCriteria.forEach(criteria => {
        evaluationData[criteria.id] = data[criteria.id] || null;
      });
      
      // Add legacy criteria scores for backward compatibility
      if (data.criteria) {
        evaluationData.criteriaScores = Object.keys(data.criteria).map(criteriaId => ({
          criteriaId: parseInt(criteriaId),
          score: data.criteria[criteriaId],
        }));
      }
      
      // Send to API using unifiedService
      let response;
      if (existingEvaluation) {
        response = await unifiedService.updateEvaluation(existingEvaluation.id, evaluationData);
      } else {
        // Status is already set to SUBMITTED above
        response = await unifiedService.submitEvaluation(evaluationData);
      }
      
      setExistingEvaluation(response.data);
      
      toast({
        title: 'تحت التقييم',
        description: 'تم تقديم التقييم بنجاح',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
      
      onClose(); // Close the confirmation modal
    } catch (error) {
      // Get appropriate error message based on error type
      let errorTitle = 'خطأ في التقديم';
      let errorDescription = 'حدث خطأ أثناء تقديم التقييم';
      
      // Handle specific error cases
      if (error.message === 'Candidate or nomination data is missing') {
        errorDescription = 'بيانات المرشح أو الترشيح غير متوفرة';
      } else if (error.statusCode === 403 && error.message.includes('لا يمكن تقييم هذا المرشح')) {
        // Branch permission error - show more specific message
        errorTitle = 'خطأ في الصلاحيات';
        errorDescription = error.message;
      } else if (error.originalError?.response?.data?.detail) {
        // Show backend error message if available
        errorDescription = error.originalError.response.data.detail;
      }
      
      toast({
        title: errorTitle,
        description: errorDescription,
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
      console.error('Error submitting evaluation:', error);
    } finally {
      setSubmitting(false);
    }
  };

  // Get score color based on value
  const getScoreColor = (score) => {
    if (score >= 8) return 'green.500';
    if (score >= 6) return 'yellow.500';
    if (score >= 4) return 'orange.500';
    return 'red.500';
  };
  
  // Get recommendation label
  const getRecommendationLabel = (recommendation) => {
    switch (recommendation) {
      case 'highly_recommended':
        return { text: 'موصى به بشدة', color: 'green' };
      case 'recommended':
        return { text: 'موصى به', color: 'teal' };
      case 'recommended_with_reservations':
        return { text: 'موصى به مع تحفظات', color: 'orange' };
      case 'not_recommended':
        return { text: 'غير موصى به', color: 'red' };
      default:
        return { text: 'لم يتم التقييم بعد', color: 'gray' };
    }
  };
  
  // Helper function to map criteria ID to candidate nomination field
  const getCandidateAnswer = (criteriaId, candidate) => {
    if (!candidate) return 'لا توجد إجابة';
    
    const fieldMap = {
      'society_knowledge_score': 'society_knowledge',
      'social_responsibility_score': 'social_responsibility_contribution',
      'vision_2030_score': 'vision_2030_contribution',
      'center_plan_score': 'center_income_ideas',
    };
    
    const field = fieldMap[criteriaId];
    return field && candidate[field] ? candidate[field] : 'لا توجد إجابة';
  };
  
  // Helper component for Grid layout
  const Grid = ({ children, templateColumns, templateRows, ...props }) => {
    return (
      <Box 
        display="grid" 
        gridGap={4} 
        gridTemplateColumns={templateColumns}
        gridTemplateRows={templateRows}
        {...props}
      >
        {children}
      </Box>
    );
  };
  
  if (loading) {
    return (
      <Container maxW="container.xl" py={8}>
        <Text textAlign="center" py={10}>
          جاري تحميل البيانات...
        </Text>
      </Container>
    );
  }
  
  return (
    <Container maxW="container.xl" py={8}>
      {/* Header */}
      <Flex justify="space-between" align="center" mb={6}>
        <HStack>
          <Heading size="lg">تقييم المرشح</Heading>
          <Badge colorScheme="blue" fontSize="md">
            {existingEvaluation?.status === 'submitted' ? 'تحت التقييم' : 'مسودة'}
          </Badge>
        </HStack>
        <Button
          leftIcon={<FiArrowLeft />}
          onClick={() => navigate('/committee/candidates')}
          variant="outline"
        >
          العودة للقائمة
        </Button>
      </Flex>
      
      {/* Candidate Info */}
      <Card mb={6}>
        <CardHeader bg="gray.50">
          <Heading size="md">معلومات المرشح</Heading>
        </CardHeader>
        <CardBody>
          <Grid
            templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }}
            templateRows="auto"
          >
            <Box>
              <Text fontWeight="bold">الاسم</Text>
              <Text>{candidate?.first_name} {candidate?.last_name}</Text>
            </Box>
            <Box>
              <Text fontWeight="bold">رقم الهوية</Text>
              <Text>{candidate?.national_id}</Text>
            </Box>
            <Box>
              <Text fontWeight="bold">الفرع</Text>
              <Text>{candidate?.branch_name || candidate?.branch?.name}</Text>
            </Box>
            <Box>
              <Text fontWeight="bold">المركز</Text>
              <Text>{candidate?.center_name || candidate?.center?.name}</Text>
            </Box>
            <Box>
              <Text fontWeight="bold">تاريخ التقديم</Text>
              <Text>{new Date(candidate?.submission_date || candidate?.created_at).toLocaleDateString('ar-SA')}</Text>
            </Box>
          </Grid>
        </CardBody>
      </Card>
      
      {/* Evaluation Form or Results */}
      {existingEvaluation?.status === 'submitted' ? (
        <Card>
          <CardHeader bg="gray.50">
            <Heading size="md">نتيجة التقييم</Heading>
          </CardHeader>
          <CardBody>
            <VStack align="stretch" spacing={6}>
              <Box>
                <Text fontWeight="bold" mb={2}>الدرجة النهائية</Text>
                <Badge
                  colorScheme={
                    getScoreColor((existingEvaluation.total_score ?? existingEvaluation.overall_score ?? existingEvaluation.totalScore)) === 'red.500' ? 'red' :
                    getScoreColor((existingEvaluation.total_score ?? existingEvaluation.overall_score ?? existingEvaluation.totalScore)) === 'orange.500' ? 'orange' :
                    getScoreColor((existingEvaluation.total_score ?? existingEvaluation.overall_score ?? existingEvaluation.totalScore)) === 'yellow.500' ? 'yellow' :
                    'green'
                  }
                  fontSize="xl"
                  py={2}
                  px={4}
                  borderRadius="md"
                >
                  {(existingEvaluation.total_score ?? existingEvaluation.overall_score ?? existingEvaluation.totalScore)} / 10
                </Badge>
              </Box>
              <Box>
                <Text fontWeight="bold" mb={2}>التوصية</Text>
                <Badge
                  colorScheme={getRecommendationLabel(existingEvaluation.recommendation).color}
                  fontSize="md"
                  py={1}
                  px={3}
                  borderRadius="md"
                >
                  {getRecommendationLabel(existingEvaluation.recommendation).text}
                </Badge>
              </Box>
              {(existingEvaluation.comments || existingEvaluation.notes) && (
                <Box>
                  <Text fontWeight="bold" mb={2}>ملاحظات</Text>
                  <Text>{existingEvaluation.comments || existingEvaluation.notes}</Text>
                </Box>
              )}
              <Box>
                <Text fontWeight="bold" mb={2}>قائمة التحقق</Text>
                <Stack spacing={2}>
                  {checklistItems.map(item => (
                    <Flex key={item.id} align="center">
                      <Box color={existingEvaluation[item.id] ? 'green.500' : 'red.500'} mr={2}>
                        {existingEvaluation[item.id] ? <FiCheck /> : <FiInfo />}
                      </Box>
                      <Text>{item.name}</Text>
                    </Flex>
                  ))}
                </Stack>
              </Box>
            </VStack>
          </CardBody>
        </Card>
      ) : (
        <form onSubmit={handleSubmit(handleSaveDraft)}>
          {/* Checklist Verification Section */}
          <Card mb={6}>
            <CardHeader bg="gray.50">
              <Heading size="md">قائمة التحقق</Heading>
            </CardHeader>
            <CardBody>
              <Text mb={4}>يجب التحقق من جميع العناصر التالية قبل المتابعة لتقييم المرشح:</Text>
              <Stack spacing={3}>
                {checklistItems.map(item => (
                  <FormControl key={item.id}>
                    <Checkbox
                      {...register(item.id)}
                      isDisabled={existingEvaluation?.status === 'submitted'}
                    >
                      {item.name}
                    </Checkbox>
                  </FormControl>
                ))}
              </Stack>
              {!checklistPassed && (
                <Alert status="warning" mt={4}>
                  <AlertIcon />
                  <AlertTitle>تنبيه!</AlertTitle>
                  <AlertDescription>
                    يجب التحقق من جميع العناصر أعلاه قبل المتابعة لتقييم المرشح.
                  </AlertDescription>
                </Alert>
              )}
            </CardBody>
          </Card>
          {/* Written Questions Evaluation Section */}
          <Card mb={6}>
            <CardHeader bg="gray.50">
              <Heading size="md">تقييم الأسئلة المكتوبة</Heading>
            </CardHeader>
            <CardBody>
              <Text mb={4}>قيّم إجابات المرشح على مقياس من 1 إلى 3 حيث 1 = ضعيف، 2 = متوسط، 3 = ممتاز</Text>
              {!checklistPassed && (
                <Alert status="error" mb={4}>
                  <AlertIcon />
                  <AlertTitle>تنبيه!</AlertTitle>
                  <AlertDescription>
                    يجب التحقق من جميع عناصر قائمة التحقق قبل إكمال التقييم.
                  </AlertDescription>
                </Alert>
              )}
              <Stack spacing={8} mt={4}>
                {writtenQuestionsCriteria.map(criteria => (
                  <Box key={criteria.id} p={4} borderWidth="1px" borderRadius="md">
                    <Text fontWeight="bold" mb={2}>{criteria.name}</Text>
                    <Box bg="gray.50" p={3} borderRadius="md" mb={4}>
                      <Text fontWeight="semibold" mb={1}>إجابة المرشح:</Text>
                      <Text>{getCandidateAnswer(criteria.id, candidate)}</Text>
                    </Box>
                    <FormControl>
                      <FormLabel>التقييم (1-3):</FormLabel>
                      <Controller
                        name={criteria.id}
                        control={control}
                        defaultValue={null}
                        render={({ field }) => (
                          <RadioGroup
                            onChange={val => field.onChange(parseInt(val))}
                            value={field.value?.toString()}
                            isDisabled={!checklistPassed || existingEvaluation?.status === 'submitted'}
                          >
                            <HStack spacing={4}>
                              <Radio value="1">1 - ضعيف</Radio>
                              <Radio value="2">2 - متوسط</Radio>
                              <Radio value="3">3 - ممتاز</Radio>
                            </HStack>
                          </RadioGroup>
                        )}
                      />
                    </FormControl>
                  </Box>
                ))}
              </Stack>
              <Box mt={6} p={4} borderWidth="1px" borderRadius="md" bg="gray.50">
                <Flex justify="space-between" align="center">
                  <Text fontWeight="bold">الدرجة الإجمالية:</Text>
                  <Badge
                    colorScheme={
                      totalScore < 3.5 ? 'red' :
                      totalScore < 6.5 ? 'orange' :
                      totalScore < 8.5 ? 'yellow' : 'green'
                    }
                    fontSize="xl"
                    py={1}
                    px={3}
                    borderRadius="md"
                  >
                    {totalScore} / 10
                  </Badge>
                </Flex>
              </Box>
            </CardBody>
          </Card>
          {/* Recommendation and Notes Section */}
          <Card mb={6}>
            <CardHeader bg="gray.50">
              <Heading size="md">التوصية والملاحظات</Heading>
            </CardHeader>
            <CardBody>
              <FormControl mb={4}>
                <FormLabel>التوصية</FormLabel>
                <Controller
                  name="recommendation"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <RadioGroup
                      onChange={field.onChange}
                      value={field.value}
                      isDisabled={!checklistPassed || existingEvaluation?.status === 'submitted'}
                    >
                      <Stack spacing={3}>
                        <Radio value="highly_recommended">موصى به بشدة</Radio>
                        <Radio value="recommended">موصى به</Radio>
                        <Radio value="recommended_with_reservations">موصى به مع تحفظات</Radio>
                        <Radio value="not_recommended">غير موصى به</Radio>
                      </Stack>
                    </RadioGroup>
                  )}
                />
              </FormControl>
              
              <FormControl>
                <FormLabel>ملاحظات إضافية</FormLabel>
                <Textarea
                  {...register('comments')}
                  placeholder="أدخل أي ملاحظات إضافية هنا"
                  isDisabled={existingEvaluation?.status === 'submitted'}
                  rows={4}
                />
              </FormControl>
            </CardBody>
          </Card>
          
          {/* Form Actions */}
          <Flex justify="space-between" mt={8} mb={4}>
            <Button
              leftIcon={<FiArrowLeft />}
              onClick={() => navigate('/committee/candidates')}
              variant="outline"
            >
              العودة
            </Button>
            
            <HStack spacing={4}>
              <Button
                leftIcon={<FiSave />}
                colorScheme="blue"
                type="submit"
                isLoading={saving}
                isDisabled={existingEvaluation?.status === 'submitted'}
              >
                حفظ كمسودة
              </Button>
              
              <Button
                leftIcon={<FiSend />}
                colorScheme="green"
                onClick={onOpen}
                isDisabled={!checklistPassed || existingEvaluation?.status === 'submitted'}
              >
                تقديم التقييم
              </Button>
            </HStack>
          </Flex>
        </form>
      )}
      
      {/* Confirmation Modal */}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>تأكيد تقديم التقييم</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>هل أنت متأكد من رغبتك في تقديم التقييم؟ لن تتمكن من تعديله بعد التقديم.</Text>
          </ModalBody>
          <ModalFooter>
            <Stack direction="row-reverse" spacing={3} w="100%" justify="flex-start">
              <Button variant="outline" onClick={onClose}>إلغاء</Button>
              <Button colorScheme="green" onClick={handleSubmitEvaluation} isLoading={submitting}>تأكيد التقديم</Button>
            </Stack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Container>
  );
};

export default EvaluationForm;
